import React from "react";
import ReactDOM from "react-dom";
import Helmet from "react-helmet";
import DirectoryItem from "./DirectoryItem";
import DirectoryEntryModel from "../Models/DirectoryEntryModel";
import DetailedEntry from "./DetailedEntry";
import Filters from "./Filters";
import axios from "../axios";
import LoadingIndicator from "./LoadingIndicator";
import locale from "../locale";
import { CancelToken } from "axios"; // This one is supposed to come from the original source
import {
  debounce,
  throttle,
  find,
  every,
  includes,
  pickBy,
  chunk,
  map,
  some,
} from "lodash";
import getCategoryNameForUrl from "../Models/GetCategoryForUrl";

function encodeTextAndTag(value) {
  return encodeURI(value.toLowerCase().replace(/ /g, "+"));
}

/**
 * Generates a URL based on the provided filters.
 *
 * @param {object} filters The list of filters to generate the URL from
 */
export function filtersToUrl(filters) {
  filters = pickBy(filters, (v) => v !== "-" && v.length > 0);
  if (Object.keys(filters).length < 1) return "/directory";

  let order = ["award", "year", "category", "distinction", "text", "tag"];
  let sort = (a, b) => {
    return order.indexOf(a) < order.indexOf(b) ? -1 : 1;
  };

  let keys = Object.keys(filters)
    .sort(sort)
    .filter((e) => order.indexOf(e) !== -1);

  let components = [];
  for (let filter of keys) {
    let value = filters[filter];

    if (filter === "category") {
      value = getCategoryNameForUrl(value);
    }

    if (filter === "text" || filter === "tag") {
      value = encodeTextAndTag(value);
    }

    components.push(`${filter}-${value}`);
  }

  if (!components.length) {
    return "/directory";
  }

  return "/directory/filtered/" + components.join("/");
}

class Directory extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.cancelTokens = [];
    this.activeRequestParams = null;

    this.originalTitle = document.getElementsByTagName("title")[0].innerHTML;
    this.filters = React.createRef();

    this.state = {
      offset: 0,
      take: 40, // How many entries to request at once
      automaticLoadTimes: 1, // How many times to reload before showing a manual reload button
      total: Infinity,
      entries: [],
      filters: this.filtersFromUrl(),
      selectedEntry: null,
      selectedEntryID: null,
      disconnected: false,
      previewMode: window.location.search.includes("key="),
      loading: false, // true -> API Request is happening
      refreshing: true, // true -> Data will be replaced, otherwise appended
    };

    this.onFilterChange = debounce(this.onFilterChange.bind(this), 200);
  }

  componentWillMount() {
    window.addEventListener(
      "scroll",
      throttle(this.checkScrollPosition.bind(this), 800)
    );
  }

  async componentDidMount() {
    if (window.ICONIC_PRE_ENTRIES) {
      let entries = window.ICONIC_PRE_ENTRIES;
      this.modelsLoaded(entries);
    } else {
      this.loadModels();
    }
    this.activateActiveItem();
    if (some(this.state.filters, (v) => v)) {
      this.onFilterChange(this.state.filters);
    }
  }

  /**
   * Creates a list of filters based on the current URL.
   */
  filtersFromUrl() {
    let match = window.location.pathname.match(/directory\/filtered\/(.+)$/);
    if (!match) return {};

    let availableDistinctions = [];
    if (this.filters.current && this.filters.current.state.filters) {
      availableDistinctions = Object.keys(
        this.filters.current.state.filters.distinctions
      );
    }

    let filters = {};
    for (let filter of match[1].split("/")) {
      let value, name;
      [name, value] = filter.split(/-(.+)/);

      if (name === "text" || name === "tag")
        value = decodeURIComponent(value.split("+").join(" "));

      if (name === "distinction") {
        if (
          availableDistinctions.length > 0 &&
          availableDistinctions.indexOf(value) === -1
        )
          continue;
      }

      if (value) filters[name] = value;
    }

    return filters;
  }

  /**
   * Activates an initial active entry based on the URL if present
   */
  activateActiveItem() {
    let match = window.location.pathname.match(/directory\/(.+)$/);
    if (!match) return;
    if (match[1].indexOf("filtered/") !== -1) return;

    let id = match[1];
    this.setState({ selectedEntryID: id }, async () => {
      let response = await axios.get(
        `/directory/${id}${window.location.search}`
      );

      // We perform this check in case the request took so long that the user has already
      // closed the detail from the other way this can load.
      if (this.state.selectedEntryID == id && !this.state.selectedEntry) {
        this.setState({
          selectedEntry: new DirectoryEntryModel(response.data),
          disconnected: true,
        });
      }
    });
  }

  /**
   * Checks the scroll position and compares it against the bottom position of the directory.
   * If they are sufficiently close and there are more entries available, it requests new ones
   * from the API.
   */
  checkScrollPosition() {
    if (this.state.entries.length == 0) return;
    if (this.state.total <= this.state.entries.length) return;
    if (this.state.selectedEntry) return;
    if (
      this.state.offset >
      this.state.take * (this.state.automaticLoadTimes - 1)
    )
      return;

    let node = ReactDOM.findDOMNode(this);
    let directoryBottom = node.offsetTop + node.clientHeight;
    let windowBottom = window.scrollY + window.innerHeight;

    let difference = directoryBottom - windowBottom;
    if (difference > 250) return;

    this.loadMore();
  }

  /**
   * Requests some additional data to be appended to the list.
   */
  loadMore() {
    let offset = this.state.offset + this.state.take;

    this.setState({ refreshing: false }, this.loadModels.bind(this, offset));
  }

  /**
   * Requests additional models with the currenet filter and a specified offset.
   * If multiple requests are executed simultaneously, only the most current one
   * will be processed.
   *
   * @param {number} offset
   */
  async loadModels(offset = 0) {
    this.setState({ loading: true });

    let params = this.state.filters;
    let hasFilters = Object.keys(params).length > 0;

    params.offset = offset;
    params.take = this.state.take;

    // We do not want to cancel the same request (e.g. while scrolling)
    if (this.activeRequestParams === params) return;

    for (let token of this.cancelTokens) {
      token.cancel("New request started");
    }

    let source = CancelToken.source();
    this.cancelTokens.push(source);

    // The request can now be cancelled, so we need to silently catch an exception
    try {
      this.activeRequestParams = params;

      let append = !this.state.refreshing;
      let response = await axios.get(
        hasFilters ? "/directory/filtered" : "/directory",
        { params, cancelToken: source.token }
      );

      this.activeRequestParams = null;

      // Although this doesn't matter too much with a cancel token, we'll keep this for now
      // just to make sure only the most recent request gets processed.

      let keys = Object.keys(this.state.filters);
      let keepRequest = every(Object.keys(params), (filter) => {
        return includes(keys, filter)
          ? this.state.filters[filter] == params[filter]
          : true;
      });

      if (!keepRequest) return;

      this.modelsLoaded(response.data, append);
    } catch (e) {
      console.log("Request cancelled", e);
    }
  }

  /**
   * Once new data has been received from the API, this function maps them to DirectoryEntryModels
   * and adds them to the entry list within the React state.
   *
   * @param {object} response The response received from the API (pagination data)
   * @param {bool} append Whether the new data should be appended to or overwrite the existing data
   */
  modelsLoaded(response, append = false) {
    let entries = response.data.map((e) => new DirectoryEntryModel(e));

    if (append) {
      entries = this.state.entries.concat(entries);
    }

    this.setState({
      entries,
      offset: response.offset,
      total: response.total,
      loading: false,
      refreshing: false,
    });

    if (this.state.selectedEntryID) {
      let selected = find(this.state.entries, [
        "id",
        this.state.selectedEntryID,
      ]);
      if (selected) {
        this.setState({ selectedEntry: selected });
      }
    }
  }

  /**
   * Change handler that gets called every time a filter changes.
   * This function is also responsible for loading new models based on the filters.
   *
   * @param {object} filters
   */
  onFilterChange(filters) {
    // Remove filters that have been set to "-"
    let adjusted = pickBy(filters, (v, name) => name === "text" || v !== "-");

    history.replaceState({}, "", filtersToUrl(filters));
    let newState = { filters: adjusted, refreshing: true };

    if (adjusted !== this.state.filters) {
      newState.offset = 0;
    }

    this.setState(newState, this.loadModels.bind(this));
  }

  /**
   * Opens a detail view for the given directory entry.
   *
   * @param {DirectoryEntryModel} entry
   */
  openModal(entry) {
    // Don't allow to open a modal when another one is already opened
    if (this.state.selectedEntry) return;
    history.replaceState({}, "", `directory/${entry.slug}`);

    this.setState({ selectedEntry: entry });
  }

  /**
   * Closes the currently opened directory entry.
   */
  closeModal() {
    history.replaceState({}, "", filtersToUrl(this.state.filters));

    this.setState({
      selectedEntry: null,
      selectedEntryID: null,
      disconnected: false,
      previewMode: false,
    });
  }

  render() {
    let rows = chunk(this.state.entries, 4);
    let insertAtIndex = Infinity;

    let insertDetailedAtIndex = -Infinity;

    let entries = map(this.state.entries, (entry, index) => {
      let invisible = false;

      if (this.state.selectedEntry) {
        let row = rows[Math.floor(index / 4)];
        invisible =
          some(row, (e) => e.id == this.state.selectedEntry.id) ||
          (this.state.disconnected && Math.floor(index / 4) === 0);
      }

      let detailed =
        this.state.selectedEntry && this.state.selectedEntry.id == entry.id;
      let item = (
        <DirectoryItem
          entry={entry}
          key={entry.id}
          preventDefault={this.props.openDirectoryItem}
          visible={!invisible}
          onClick={this.openModal.bind(this, entry)}
        />
      );

      // If the item is at index 0, we also need to store the list index so we can
      // later add the detailed entry at that place. For any other index, just
      // return now.
      if (!invisible || index % 4 !== 0) return item;

      insertDetailedAtIndex = index;
      return item;
    });

    if (insertDetailedAtIndex >= 0) {
      entries.splice(
        insertDetailedAtIndex,
        0,
        <DetailedEntry
          entry={this.state.selectedEntry}
          closeCallback={this.closeModal.bind(this)}
          onTagSelection={(tag) => {
            let filters = this.state.filters;
            filters.tag = tag;

            this.onFilterChange(filters);
          }}
          scrollTo={true}
          key="detail"
        />
      );
    }

    // Set up wrapper classes
    let classes = ["directory"];
    if (this.state.selectedEntry) classes.push("detail");
    if (this.state.loading && this.state.refreshing) classes.push("refreshing");

    // Text for when no entries are available
    let noentries = null;
    if (!entries.length) {
      noentries = <p className="noresults">{locale.noResults}</p>;
    }

    // The manual reload button
    let loadMore = null;
    if (
      this.state.total > this.state.entries.length &&
      entries.length > this.state.take * this.state.automaticLoadTimes
    ) {
      loadMore = (
        <button className="load-more" onClick={this.loadMore.bind(this)}>
          {locale.loadMore}
        </button>
      );
    }

    let selectedTag = null;
    if (this.state.filters.tag && this.filters.current) {
      let correctTag = find(
        this.filters.current.state.filters.tags[iconicLocale],
        (tag) =>
          tag === this.state.filters.tag ||
          encodeTextAndTag(tag) === this.state.filters.tag ||
          tag.toLowerCase() === this.state.filters.tag.toLowerCase()
      );

      if (correctTag) {
        selectedTag = (
          <span className="tag">
            {correctTag}
            <a
              href="/directory"
              className="close"
              onClick={(e) => {
                e.preventDefault();
                let filters = this.state.filters;
                delete filters.tag;
                this.onFilterChange(filters);
              }}
            />
          </span>
        );
      }
    }

    let filters = null;

    if (!this.props.hideFilter) {
      filters = (
        <Filters
          values={this.state.filters}
          onChange={this.onFilterChange}
          ref={this.filters}
        />
      );
    }

    let previewIndicator;
    if (this.state.previewMode) {
      previewIndicator = <div className="preview-indicator" />;
      classes.push("preview-mode");
    }

    return (
      <div className={classes.join(" ")}>
        {selectedTag}
        {filters}

        <Helmet>
          <title>{this.originalTitle}</title>
        </Helmet>
        {previewIndicator}
        <LoadingIndicator
          active={this.state.loading && this.state.refreshing}
        />

        <div className="directory-entries">
          {noentries}

          {entries}
        </div>

        <LoadingIndicator
          active={this.state.loading && !this.state.refreshing}
        />
        {loadMore}
      </div>
    );
  }
}

Directory.defaultProps = {
  hideFilter: false,
  openDirectoryItem: true,
};

export default Directory;
